/**
 * Product-card registry + back-compat resolver — S2 of the composable-themes
 * plan (docs/plans/2026-07-17-composable-themes.md §8).
 *
 * Mirrors the section-schema pattern: each card declares its OWN controls, so
 * the inspector renders exactly the fields that card supports and adding
 * card #N touches no shared type. This supersedes the ~10 flat `productCard*`
 * tokens on `ThemeTokens`, which remain readable in v1 via `resolveProductCard`
 * (plan non-goals: "Deleting the flat productCard* tokens in v1").
 *
 * S2 is purely additive: the buyer/editor still read the flat tokens today.
 * This registry + resolver exist for the merge (S3), gallery (S4), and admin
 * (S5) to consume, and to make card #11–20 cheap once the vocabulary grows.
 *
 * The field option lists here mirror the flat token fields in `theme.ts`
 * (`layoutFields`) one-for-one. They are intentionally authored longhand with
 * card-local, un-prefixed ids (`radius`, not `productCardRadius`) because a
 * card's `settings` is its own namespace — `showIf` keys reference sibling
 * setting ids. Keep the two in sync until the flat tokens are removed.
 */
// ---------------------------------------------------------------------------
// Shared control fields (reused across every card).
// ---------------------------------------------------------------------------
const radiusField = {
    id: "radius",
    type: "select",
    label: { ar: "حواف كارت المنتج", en: "Product card radius" },
    helpText: {
        ar: "درجة استدارة حواف صورة كارت المنتج.",
        en: "How rounded the product-card image corners are.",
    },
    options: [
        { value: "0", label: { ar: "حادة", en: "Sharp" } },
        { value: "0.5rem", label: { ar: "صغيرة", en: "Small" } },
        { value: "0.75rem", label: { ar: "متوسطة", en: "Medium" } },
        { value: "1rem", label: { ar: "كبيرة", en: "Large" } },
        { value: "1.5rem", label: { ar: "كبيرة جدًا", en: "Extra large" } },
    ],
    default: "0",
};
const shadowField = {
    id: "shadow",
    type: "select",
    label: { ar: "ظل كارت المنتج", en: "Product card shadow" },
    helpText: {
        ar: "قوة الظل تحت كارت المنتج.",
        en: "Drop-shadow depth under the product card.",
    },
    options: [
        { value: "none", label: { ar: "بدون", en: "None" } },
        { value: "sm", label: { ar: "خفيف", en: "Subtle" } },
        { value: "md", label: { ar: "متوسط", en: "Medium" } },
        { value: "lg", label: { ar: "قوي", en: "Large" } },
    ],
    default: "none",
};
const imageFitField = {
    id: "imageFit",
    type: "select",
    label: { ar: "ملء صورة المنتج", en: "Product image fit" },
    helpText: {
        ar: "تغطية: تملأ الإطار مع قص. احتواء: تظهر الصورة كاملة.",
        en: "Cover fills + crops; contain shows the whole image.",
    },
    options: [
        { value: "cover", label: { ar: "تغطية", en: "Cover" } },
        { value: "contain", label: { ar: "احتواء", en: "Contain" } },
    ],
    default: "cover",
};
const imageBgField = {
    id: "imageBg",
    type: "color",
    label: { ar: "خلفية صورة المنتج", en: "Product image background" },
    helpText: {
        ar: 'لون الفراغ ورا الصورة لما تختار "احتواء" — مفيد للصور بخلفية غامقة.',
        en: 'Fills the letterbox area behind "contain" images — useful for dark-background photos.',
    },
    // Card-local sibling reference (was `productCardImageFit` in the flat schema).
    showIf: { imageFit: "contain" },
};
const showColorsField = {
    id: "showColors",
    type: "checkbox",
    label: { ar: "عرض ألوان المنتج", en: "Show color swatches" },
    helpText: {
        ar: "يعرض دوائر صغيرة بألوان المنتج المتاحة على الكارت.",
        en: "Shows small dots of the product's available colors on the card.",
    },
    default: false,
};
/** Controls every card exposes. */
const COMMON_CARD_FIELDS = [
    radiusField,
    shadowField,
    imageFitField,
    imageBgField,
    showColorsField,
];
// ---------------------------------------------------------------------------
// "Card" style only — the always-visible add-to-order button + its options.
// In the flat schema these were gated by `showIf: { productCardStyle: 'card' }`;
// here they simply belong to the `card` schema, so no cross-card gate is needed.
// ---------------------------------------------------------------------------
const addButtonFields = [
    {
        id: "showAddButton",
        type: "checkbox",
        label: { ar: "عرض زر الإضافة للطلب", en: "Show add-to-order button" },
        helpText: {
            ar: 'يظهر زر الإضافة أسفل كل كارت في شكل "بطاقة مع زر".',
            en: "Shows the add button under every card in the Card style.",
        },
        default: true,
    },
    {
        id: "addButtonStyle",
        type: "select",
        label: { ar: "شكل زر الإضافة", en: "Add button style" },
        options: [
            { value: "outline", label: { ar: "محدد", en: "Outline" } },
            { value: "solid", label: { ar: "ممتلئ", en: "Filled" } },
        ],
        default: "outline",
        showIf: { showAddButton: true },
    },
    {
        id: "addButtonRadius",
        type: "select",
        label: { ar: "حواف زر الإضافة", en: "Add button radius" },
        options: [
            { value: "0", label: { ar: "حادة", en: "Sharp" } },
            { value: "6px", label: { ar: "صغيرة", en: "Small" } },
            { value: "10px", label: { ar: "متوسطة", en: "Medium" } },
            { value: "16px", label: { ar: "كبيرة", en: "Large" } },
            { value: "9999px", label: { ar: "حبة", en: "Pill" } },
        ],
        default: "9999px",
        showIf: { showAddButton: true },
    },
    {
        id: "addButtonLabel",
        type: "text",
        i18n: true,
        label: { ar: "نص زر الإضافة", en: "Add button label" },
        helpText: {
            ar: "سيبه فاضي لاستخدام النص الافتراضي.",
            en: "Leave empty to use the default label.",
        },
        default: { ar: "ضيف على الطلب", en: "Add to order" },
        showIf: { showAddButton: true },
    },
];
// ---------------------------------------------------------------------------
// The registry. Seven cards today, one-for-one with the legacy
// `productCardStyle` union; only `card` carries the add-button controls.
// ---------------------------------------------------------------------------
function makeCard(id, name, extraFields = []) {
    return {
        id,
        name,
        thumbnail: `/product-cards/${id}.png`,
        settings: [...COMMON_CARD_FIELDS, ...extraFields],
    };
}
/** Default card id — mirrors `productCardStyle` default in `theme.ts`. */
export const DEFAULT_PRODUCT_CARD_ID = "grid";
/** All product-card designs, keyed by id. */
export const productCardRegistry = {
    grid: makeCard("grid", { ar: "كلاسيكي", en: "Classic" }),
    clean: makeCard("clean", { ar: "بسيط", en: "Minimal" }),
    overlay: makeCard("overlay", { ar: "تراكب", en: "Overlay" }),
    compact: makeCard("compact", { ar: "مدمج", en: "Compact" }),
    card: makeCard("card", { ar: "بطاقة مع زر", en: "Card" }, addButtonFields),
    simple: makeCard("simple", { ar: "مجرد", en: "Simple" }),
    // Composable-themes S7 additions (ported from EasyOrders references).
    "soft-tile": makeCard("soft-tile", { ar: "بلاطة ناعمة", en: "Soft tile" }),
    editorial: makeCard("editorial", { ar: "تحريري", en: "Editorial" }),
    "centered-accent": makeCard("centered-accent", {
        ar: "موسّط بلمسة",
        en: "Centered accent",
    }),
    "rounded-shadow": makeCard("rounded-shadow", {
        ar: "ظل ناعم",
        en: "Rounded shadow",
    }),
    "list-row": makeCard("list-row", { ar: "صف أفقي", en: "List row" }),
    bordered: makeCard("bordered", { ar: "محدد", en: "Bordered" }),
    framed: makeCard("framed", { ar: "إطار", en: "Framed" }),
    "bold-tile": makeCard("bold-tile", { ar: "بلاطة جريئة", en: "Bold tile" }),
    catalog: makeCard("catalog", { ar: "كتالوج", en: "Catalog" }),
    showcase: makeCard("showcase", { ar: "عرض", en: "Showcase" }),
    // Dark gaming card (EasyOrders blast / GUNNERS) for the `impact` family.
    // TODO: ships with the showcase thumbnail as a placeholder until a dedicated
    // `/product-cards/gamer.png` asset is added.
    gamer: {
        ...makeCard("gamer", { ar: "ألعاب", en: "Gamer" }),
        thumbnail: "/product-cards/showcase.png",
    },
};
/** Ordered card ids (registry insertion order). */
export const PRODUCT_CARD_IDS = Object.keys(productCardRegistry);
/** Returns the schema for a product-card id, or `undefined`. */
export function getProductCardSchema(id) {
    return productCardRegistry[id];
}
// ---------------------------------------------------------------------------
// Back-compat resolver.
// ---------------------------------------------------------------------------
/**
 * Resolve a store's product-card selection to a structured `ProductCardInstance`.
 *
 * Prefers `tokens.productCard` (what new writers set). Falls back to deriving
 * one from the deprecated flat `productCard*` tokens so legacy stores keep
 * rendering identically. Card-only fields (`showAddButton` etc.) are carried
 * over only when the resolved card is `card`, matching the flat schema's
 * `showIf` gate.
 *
 * Only defined values are copied — absent keys let downstream consumers apply
 * the schema `default`, exactly as the flat token reads did with `?? ctx`.
 */
export function resolveProductCard(tokens) {
    if (tokens?.productCard)
        return tokens.productCard;
    const type = tokens?.productCardStyle ?? DEFAULT_PRODUCT_CARD_ID;
    const settings = {};
    const put = (key, value) => {
        if (value !== undefined)
            settings[key] = value;
    };
    put("radius", tokens?.productCardRadius);
    put("shadow", tokens?.productCardShadow);
    put("imageFit", tokens?.productCardImageFit);
    put("imageBg", tokens?.productCardImageBg);
    put("showColors", tokens?.productCardShowColors);
    if (type === "card") {
        put("showAddButton", tokens?.productCardShowAddButton);
        put("addButtonStyle", tokens?.productCardAddButtonStyle);
        put("addButtonRadius", tokens?.productCardAddButtonRadius);
        put("addButtonLabel", tokens?.productCardAddButtonLabel);
    }
    return { type, settings };
}
//# sourceMappingURL=product-cards.js.map